import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Loader2,
  File,
  FileSpreadsheet,
  Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'pitch_deck', label: 'Pitch Deck' },
  { value: 'term_sheet', label: 'Term Sheet' },
  { value: 'financials', label: 'Financials' },
  { value: 'other', label: 'Other' }
];

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return FileText;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) return FileSpreadsheet;
  if (contentType?.includes('image')) return ImageIcon;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentTypeLabel = ({ type }) => {
  const colors = {
    pitch_deck: 'bg-blue-100 text-blue-700',
    term_sheet: 'bg-purple-100 text-purple-700',
    financials: 'bg-emerald-100 text-emerald-700',
    other: 'bg-slate-100 text-slate-700'
  };
  
  return (
    <Badge className={colors[type] || colors.other}>
      {type.replace('_', ' ')}
    </Badge>
  );
};

export default function DocumentManager({ investmentId, investmentName }) {
  const { getAuthHeaders, token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [documentType, setDocumentType] = useState('pitch_deck');
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/investments/${investmentId}/documents`,
        { headers: getAuthHeaders() }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/api/investments/${investmentId}/documents?document_type=${documentType}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/${doc.id}/download`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/documents/${doc.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Fetch documents when component mounts or investmentId changes
  useState(() => {
    if (investmentId) {
      fetchDocuments();
    }
  }, [investmentId]);

  return (
    <div className="space-y-4" data-testid="document-manager">
      <div className="flex items-center justify-between">
        <h4 className="font-heading font-semibold text-slate-900">Documents</h4>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setUploadDialogOpen(true)}
          data-testid="upload-document-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No documents uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => {
            const FileIcon = getFileIcon(doc.content_type);
            return (
              <div 
                key={doc.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                    <FileIcon className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                      {doc.original_filename}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <DocumentTypeLabel type={doc.document_type} />
                      <span className="text-xs text-slate-400">{formatFileSize(doc.size)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDownload(doc)}
                    data-testid={`download-${doc.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(doc)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`delete-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for {investmentName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>File</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="block w-full text-sm text-slate-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-medium
                    file:bg-[#1E3A5F] file:text-white
                    hover:file:bg-[#152c4a]
                    file:cursor-pointer cursor-pointer"
                  data-testid="file-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG (Max 50MB)
              </p>
            </div>

            {uploading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
                <span className="ml-2 text-slate-600">Uploading...</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
